
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../../api/axios";
import {
  deleteProduct,
  fetchProducts,
} from "../../features/products/productsSlice";
import { fetchCategories } from "../../features/categories/categoriesSlice";
import CategoriesFilter from "../CategoriesList";
import {
  FiPlus,
  FiSearch,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiPackage,
  FiBox,
  FiTrendingUp,
} from "react-icons/fi";

function DashboardProducts() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { items: products = [] } = useSelector((state) => state.products);

  const selectedCategory = useSelector((state) => state.filter.category);

  const [searchTerm, setSearchTerm] = useState("");

  const dir = i18n.dir();
  const isArabic = i18n.language === "ar";

  // Fetch products
  useEffect(() => {
    dispatch(fetchProducts());
    dispatch(fetchCategories());
  }, [dispatch]);

  // Delete product
  const delProduct = (id) => {
    Swal.fire({
      title: t("deleteProduct.confirm_title"),
      text: t("deleteProduct.confirm_text"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#B5183F",
      cancelButtonColor: "#999",
      confirmButtonText: t("deleteProduct.confirm_yes"),
      cancelButtonText: t("deleteProduct.confirm_no"),
      buttonsStyling: true,
      reverseButtons: isArabic,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/products/${id}`);

          dispatch(deleteProduct(id));

          Swal.fire({
            icon: "success",
            title: t("deleteProduct.success_title"),
            text: t("deleteProduct.success_text"),
            confirmButtonColor: "#B5183F",
          });
        } catch (err) {
          Swal.fire({
            icon: "error",
            title: t("deleteProduct.error_title"),
            text: t("deleteProduct.error_text"),
            confirmButtonColor: "#B5183F",
          });
        }
      }
    });
  };

  // Filter products
  const filteredItems = products.filter((item) => {
    const matchCategory =
      selectedCategory === "all" ||
      !selectedCategory ||
      item.category_id === parseInt(selectedCategory);

    const name = isArabic ? item.name_ar : item.name_en;

    const matchSearch = name?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchCategory && matchSearch;
  });

  const totalProducts = products.length;

  const availableProducts = products.filter(
    (product) => Number(product.stock) > 0,
  ).length;

  const outOfStock = products.filter(
    (product) => Number(product.stock) <= 0,
  ).length;

  const bestSelling = products.filter(
    (product) =>
      product.best_selling === true ||
      product.best_selling === 1 ||
      product.best_selling === "1",
  ).length;

  return (
    <div
      dir={dir}
      className="py-2"
      style={{
        color: "#272727",
      }}
    >
      {/* Header */}
      <div
        className="rounded-4 p-md-5 mb-2"
        style={{
          background: "linear-gradient(135deg, #B5183F 0%, #8F1232 100%)",
          position: "relative",
          overflow: "hidden",
          boxShadow: "0 15px 40px rgba(181,24,63,.14)",
        }}
      >
        <div
          style={{
            position: "absolute",
            width: "220px",
            height: "220px",
            borderRadius: "50%",
            backgroundColor: "rgba(255,255,255,.06)",
            top: "-100px",
            right: "-60px",
          }}
        />

        <div
          style={{
            position: "absolute",
            width: "140px",
            height: "140px",
            borderRadius: "50%",
            backgroundColor: "rgba(255,255,255,.05)",
            bottom: "-70px",
            left: "20%",
          }}
        />

        <div
          className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-4"
          style={{
            position: "relative",
            zIndex: 2,
          }}
        >
          <div>
            <h1
              className="fw-bold mb-2"
              style={{
                color: "#fff",
                fontSize: "clamp(1.6rem, 3vw, 2.3rem)",
              }}
            >
              {t("dashboard.products")} {filteredItems.length}
            </h1>
          </div>

          <Link
            to="/addProduct"
            className="btn d-flex align-items-center justify-content-center gap-2 rounded-3 px-4 py-3"
            style={{
              backgroundColor: "#fff",
              color: "#B5183F",
              border: "none",
              fontWeight: 700,
              textDecoration: "none",
              minWidth: "165px",
              boxShadow: "0 8px 25px rgba(0,0,0,.12)",
            }}
          >
            <FiPlus size={18} />
            {t("dashboard.add_product")}
          </Link>
        </div>
      </div>

      {/* Stats */}

      <div className="row g-3 mb-1">
        <StatCard
          icon={<FiBox />}
          value={totalProducts}
          label={t("dashboard.total_products", "Total Products")}
        />

        <StatCard
          icon={<FiPackage />}
          value={availableProducts}
          label={t("dashboard.available", "Available")}
          green
        />

        <StatCard
          icon={<FiTrendingUp />}
          value={bestSelling}
          label={t("table.best_selling", "Best Selling")}
        />

        <StatCard
          icon={<FiBox />}
          value={outOfStock}
          label={t("dashboard.out_of_stock", "Out of Stock")}
          danger
        />
      </div>

      {/* Filters */}

      <div
        className="rounded-4 p-3 p-md-4 mb-4"
        style={{
          backgroundColor: "#fff",
          border: "1px solid #EEE5E8",
          boxShadow: "0 8px 30px rgba(39,39,39,.035)",
        }}
      >
        <div className="row g-3 align-items-center">
          <div className="col-12 col-lg-6">
            <div className="position-relative">
              <FiSearch
                className={`position-absolute top-50 translate-middle-y ${
                  isArabic ? "end-0 me-3" : "start-0 ms-3"
                }`}
                size={17}
                style={{
                  color: "#B5183F",
                  pointerEvents: "none",
                }}
              />

              <input
                type="text"
                className={`form-control rounded-3 ${
                  isArabic ? "pe-5" : "ps-5"
                }`}
                placeholder={t("table.search")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  height: "48px",
                  backgroundColor: "#FDFBFC",
                  border: "1px solid #EEE5E8",
                  boxShadow: "none",
                  fontSize: "13px",
                }}
              />
            </div>
          </div>

          <div className="col-12 col-lg-6">
            <CategoriesFilter />
          </div>
        </div>
      </div>

      {/* Products */}

      <div
        className="rounded-4 overflow-hidden"
        style={{
          backgroundColor: "#fff",
          border: "1px solid #EEE5E8",
          boxShadow: "0 10px 35px rgba(39,39,39,.04)",
        }}
      >

        <div className="table-responsive">
          <table
            className="table mb-0 align-middle"
            style={{
              minWidth: "1000px",
            }}
          >
            <thead>
              <tr
                style={{
                  backgroundColor: "#FBF8F9",
                }}
              >
                <TableHeader>{t("table.id")}</TableHeader>

                <TableHeader>{t("table.name")}</TableHeader>

                <TableHeader>{t("table.price")}</TableHeader>

                <TableHeader>{t("table.image")}</TableHeader>

                <TableHeader>{t("table.description")}</TableHeader>

                <TableHeader>{t("table.stock")}</TableHeader>

                <TableHeader>{t("table.best_selling")}</TableHeader>

                <TableHeader center>{t("table.operation")}</TableHeader>
              </tr>
            </thead>

            <tbody>
              {filteredItems.length > 0 ? (
                filteredItems.map((product) => {
                  const productName = isArabic
                    ? product.name_ar
                    : product.name_en;

                  const description = isArabic
                    ? product.description_ar
                    : product.description_en;

                  const isOutOfStock = Number(product.stock) <= 0;

                  const isBestSelling =
                    product.best_selling === true ||
                    product.best_selling === 1 ||
                    product.best_selling === "1";

                  return (
                    <tr
                      key={product.id}
                      style={{
                        borderBottom: "1px solid #F3ECEF",
                      }}
                    >
                      <td className="px-3">
                        <span
                          style={{
                            color: "#A5A5A5",
                            fontSize: "12px",
                            fontWeight: 700,
                          }}
                        >
                          #{product.id}
                        </span>
                      </td>

                      <td>
                        <div
                          className="fw-bold"
                          style={{
                            color: "#272727",
                            fontSize: "13px",
                            maxWidth: "180px",
                          }}
                        >
                          {productName}
                        </div>
                      </td>

                      <td>
                        <span
                          className="fw-bold"
                          style={{
                            color: "#B5183F",
                            fontSize: "13px",
                          }}
                        >
                          {product.price} EGP
                        </span>
                      </td>

                      <td>
                        <div
                          className="rounded-3 d-flex align-items-center justify-content-center overflow-hidden"
                          style={{
                            width: "64px",
                            height: "64px",
                            backgroundColor: "#FDFBFC",
                            border: "1px solid #EEE5E8",
                          }}
                        >
                          <img
                            src={product.image_url}
                            alt={productName}
                            loading="lazy"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </div>
                      </td>

                      <td>
                        <div
                          title={description}
                          style={{
                            maxWidth: "190px",
                            color: "#777",
                            fontSize: "12px",
                            lineHeight: "1.7",
                          }}
                        >
                          {description?.length > 65
                            ? `${description.slice(0, 65)}...`
                            : description}
                        </div>
                      </td>

                      <td>
                        <span
                          className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill"
                          style={{
                            backgroundColor: isOutOfStock
                              ? "#F9E9EE"
                              : "#DEEDE8",
                            color: isOutOfStock ? "#B5183F" : "#397064",
                            fontSize: "11px",
                            fontWeight: 700,
                          }}
                        >
                          <span
                            style={{
                              width: "6px",
                              height: "6px",
                              borderRadius: "50%",
                              backgroundColor: isOutOfStock
                                ? "#B5183F"
                                : "#397064",
                            }}
                          />

                          {product.stock}
                        </span>
                      </td>

                      <td>
                        <span
                          className="px-3 py-2 rounded-pill"
                          style={{
                            backgroundColor: isBestSelling
                              ? "#FFF3D9"
                              : "#F5F5F5",
                            color: isBestSelling ? "#A36B00" : "#999",
                            fontSize: "11px",
                            fontWeight: 700,
                          }}
                        >
                          {isBestSelling ? "Yes" : "No"}
                        </span>
                      </td>

                      <td>
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <ActionButton
                            to={`/products/${product.id}/edit`}
                            icon={<FiEdit2 size={15} />}
                            color="#B5183F"
                            background="#F9E9EE"
                            title="Edit"
                          />

                          <button
                            type="button"
                            onClick={() => delProduct(product.id)}
                            title="Delete"
                            className="border-0 d-flex align-items-center justify-content-center rounded-3"
                            style={{
                              width: "36px",
                              height: "36px",
                              backgroundColor: "#FFF0F2",
                              color: "#D33A52",
                            }}
                          >
                            <FiTrash2 size={15} />
                          </button>

                          <ActionButton
                            to={`/products/${product.id}`}
                            icon={<FiEye size={15} />}
                            color="#397064"
                            background="#DEEDE8"
                            title="View"
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-5">
                    <div className="d-flex flex-column align-items-center">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          width: "70px",
                          height: "70px",
                          backgroundColor: "#F9E9EE",
                          color: "#B5183F",
                        }}
                      >
                        <FiBox size={28} />
                      </div>

                      <h6
                        className="fw-bold mt-3 mb-1"
                        style={{
                          color: "#555",
                        }}
                      >
                        {t("table.no_data", "No products found")}
                      </h6>

                      <small
                        style={{
                          color: "#AAA",
                        }}
                      >
                        Try changing your search or filter.
                      </small>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon, value, label, green, danger }) {
  return (
    <div className="col-6 col-xl-3">
      <div
        className="h-100 rounded-4 p-3 p-md-4"
        style={{
          backgroundColor: "#fff",
          border: "1px solid #EEE5E8",
          boxShadow: "0 8px 25px rgba(39,39,39,.035)",
        }}
      >
        <div className="d-flex align-items-center justify-content-between gap-3">
          <div>
            <div
              className="fw-bold mb-1"
              style={{
                color: "#272727",
                fontSize: "clamp(1.3rem, 3vw, 1.7rem)",
              }}
            >
              {value}
            </div>

            <small
              style={{
                color: "#999",
                fontSize: "11px",
              }}
            >
              {label}
            </small>
          </div>

          <div
            className="d-flex align-items-center justify-content-center rounded-3"
            style={{
              width: "44px",
              height: "44px",
              flexShrink: 0,
              backgroundColor: danger
                ? "#FFF0F2"
                : green
                  ? "#DEEDE8"
                  : "#F9E9EE",
              color: danger ? "#D33A52" : green ? "#397064" : "#B5183F",
            }}
          >
            {icon}
          </div>
        </div>
      </div>
    </div>
  );
}

function TableHeader({ children, center }) {
  return (
    <th
      className={`py-3 ${center ? "text-center" : ""}`}
      style={{
        color: "#888",
        fontSize: "10px",
        fontWeight: 800,
        whiteSpace: "nowrap",
        borderBottom: "1px solid #EEE5E8",
        letterSpacing: ".3px",
      }}
    >
      {children}
    </th>
  );
}

function ActionButton({ to, icon, color, background, title }) {
  return (
    <Link
      to={to}
      title={title}
      className="d-flex align-items-center justify-content-center rounded-3"
      style={{
        width: "36px",
        height: "36px",
        backgroundColor: background,
        color,
        textDecoration: "none",
        transition: "all .2s ease",
      }}
    >
      {icon}
    </Link>
  );
}

export default DashboardProducts;
